import { getAnalytics, logEvent } from "firebase/analytics";

/**
 * Google Analytics Service for STI Grant Uganda Hackathon
 *
 * This service tracks key user interactions for the hackathon platform:
 *
 * CRITICAL EVENTS TRACKED:
 * 1. Apply Now button clicks (event detail page, mobile, sticky CTA)
 * 2. Application form submission
 * 3. Team creation and management
 * 4. File uploads (pitch deck)
 * 5. Category selection
 * 6. Dashboard access
 * 7. Key tab navigation (application, teams)
 * 8. Share button interactions
 *
 * These events provide insights into user engagement without impacting performance.
 */

// Initialize analytics - this is already done in firebase-config.js
let analytics;

try {
  analytics = getAnalytics();
} catch (error) {
  console.warn("Analytics not available:", error);
}

/**
 * Track custom events with Google Analytics
 * @param {string} eventName - The name of the event to track
 * @param {Object} eventParameters - Additional parameters to send with the event
 */
export const trackEvent = (eventName, eventParameters = {}) => {
  if (!analytics) {
    console.warn("Analytics not initialized, cannot track event:", eventName);
    return;
  }

  try {
    logEvent(analytics, eventName, {
      timestamp: new Date().toISOString(),
      ...eventParameters,
    });
    console.log("Event tracked:", eventName, eventParameters);
  } catch (error) {
    console.error("Error tracking event:", error);
  }
};

/**
 * Track hackathon-specific events
 */
export const trackHackathonEvent = (action, additionalData = {}) => {
  trackEvent("hackathon_interaction", {
    action: action,
    event_category: "hackathon",
    ...additionalData,
  });
};

/**
 * Track application form events
 */
export const trackApplicationEvent = (
  action,
  step = null,
  additionalData = {}
) => {
  trackEvent("application_interaction", {
    action: action,
    event_category: "application",
    step: step,
    ...additionalData,
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (
  buttonName,
  context = null,
  additionalData = {}
) => {
  trackEvent("button_click", {
    button_name: buttonName,
    context: context,
    event_category: "ui_interaction",
    ...additionalData,
  });
};

/**
 * Track navigation events
 */
export const trackNavigation = (from, to, additionalData = {}) => {
  trackEvent("navigation", {
    from_page: from,
    to_page: to,
    event_category: "navigation",
    ...additionalData,
  });
};

/**
 * Track team events
 */
export const trackTeamEvent = (action, teamData = {}) => {
  trackEvent("team_interaction", {
    action: action,
    event_category: "team",
    ...teamData,
  });
};

// Predefined event types for hackathon tracking
export const HACKATHON_EVENTS = {
  APPLY_NOW_CLICKED: "apply_now_clicked",
  APPLICATION_STARTED: "application_started",
  APPLICATION_STEP_COMPLETED: "application_step_completed",
  APPLICATION_SUBMITTED: "application_submitted",
  TEAM_CREATED: "team_created",
  TEAM_JOINED: "team_joined",
  PITCH_DECK_UPLOADED: "pitch_deck_uploaded",
  VIDEO_PITCH_SUBMITTED: "video_pitch_submitted",
  OVERVIEW_VIDEO_PLAYED: "overview_video_played",
  CATEGORY_SELECTED: "category_selected",
  DASHBOARD_ACCESSED: "dashboard_accessed",
  RESOURCES_ACCESSED: "resources_accessed",
  TEAMS_TAB_ACCESSED: "teams_tab_accessed",
  SOCIAL_MEDIA_SHARED: "social_media_shared",
};
